import { useEffect, useState } from 'react';
import api from './client';

/** Admin-editable content pages. */
export type PageSlug = 'privacy-policy' | 'terms-of-service' | 'delete-account';

export const PAGE_SLUGS: PageSlug[] = ['privacy-policy', 'terms-of-service', 'delete-account'];

export interface ContentPage {
  /** Fixed API key; never changes. */
  slug: PageSlug;
  /** Where this site serves the page, e.g. "/privacy-policy" (admins can change it). */
  path: string;
  /** Full URL on another domain that links point to instead, or null. */
  externalUrl: string | null;
  /** The URL to link to: externalUrl when set, otherwise this site's address. */
  url: string;
  title: string;
  /** Sanitized HTML from the admin editor. */
  content: string;
  /** YYYY-MM-DD, or null when no date is shown. */
  effectiveDate: string | null;
  updatedAt: string;
}

export interface ContentPageUpdate {
  title?: string;
  content?: string;
  effectiveDate?: string | null;
  /** New address: a path on this site ("/privacy") or a full URL on any domain. */
  url?: string;
}

export interface PageUrl {
  slug: PageSlug;
  title: string;
  path: string;
  externalUrl: string | null;
  url: string;
  updatedAt: string;
}

interface PageListResponse {
  status: number;
  pages: PageUrl[];
}

interface PageResponse {
  status: number;
  message?: string;
  page: ContentPage;
}

/** GET /api/pages/:slug (public) */
export const getPage = async (slug: PageSlug): Promise<ContentPage> => {
  const res = await api.get<PageResponse>(`/pages/${slug}`);
  return res.data.page;
};

/** PUT /api/pages/:slug (admin) */
export const updatePage = async (
  slug: PageSlug,
  payload: ContentPageUpdate
): Promise<ContentPage> => {
  const res = await api.put<PageResponse>(`/pages/${slug}`, payload);
  if (payload.url !== undefined) pageUrlsRequest = null;
  return res.data.page;
};

// One request per page load, shared by every link and the route resolver.
let pageUrlsRequest: Promise<PageUrl[]> | null = null;

/** GET /api/pages (public) — every page's path on this site and the URL links go to. */
export const getPageUrls = (): Promise<PageUrl[]> => {
  if (!pageUrlsRequest) {
    pageUrlsRequest = api.get<PageListResponse>('/pages').then(
      (res) => res.data.pages,
      (err) => {
        pageUrlsRequest = null;
        throw err;
      }
    );
  }
  return pageUrlsRequest;
};

export interface PageHref {
  /** A path on this site, or a full URL when the admin set an external one. */
  href: string;
  external: boolean;
}

/**
 * Where links to a page should go: "/privacy-policy" until an admin changes
 * it, or the external URL they set. Starts at the default path so links
 * render straight away; the old address keeps working, so a stale link is fine.
 */
export const usePageHref = (slug: PageSlug): PageHref => {
  const [target, setTarget] = useState<PageHref>({ href: `/${slug}`, external: false });
  useEffect(() => {
    let alive = true;
    getPageUrls()
      .then((pages) => {
        const page = pages.find((p) => p.slug === slug);
        if (!alive || !page) return;
        setTarget(
          page.externalUrl
            ? { href: page.externalUrl, external: true }
            : { href: page.path, external: false }
        );
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [slug]);
  return target;
};

/** "2026-09-29" -> "September 29, 2026" (read as a calendar date, not local time). */
export const formatEffectiveDate = (date: string): string =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
